import jsPDF from 'jspdf';
import { format, parseISO } from 'date-fns';
import { nl } from 'date-fns/locale';
import type { PiekAnalyse, QuarterData, DayConsumptionStatus } from '@/types/piekanalyse';
import { calculateDailyKWh, calculateTotalKWh, calculateMonthlyKWh, getDayStatus, calculateThreshold } from '@/lib/piekvermoigenParser';

const DUTCH_MONTHS = [
  'Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni',
  'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'
];

const formatKWh = (value: number): string => {
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)} MWh`;
  }
  return `${Math.round(value)} kWh`;
};

export async function generateConsumptionReportPDF(
  analysis: PiekAnalyse,
  targetSolutionKWh: number
): Promise<void> {
  if (!analysis.raw_data) {
    throw new Error('Geen dagdata beschikbaar voor rapportage');
  }

  const pdf = new jsPDF();
  const pageWidth = pdf.internal.pageSize.getWidth();
  const margin = 20;
  let y = 20;

  const contractedCapacity = analysis.config?.contractedCapacity || 0;
  const threshold = calculateThreshold(contractedCapacity, targetSolutionKWh);
  const totalKWh = calculateTotalKWh(analysis.raw_data);
  const monthlyKWh = calculateMonthlyKWh(analysis.raw_data);

  // Calculate day statuses
  const dayStatuses: DayConsumptionStatus[] = Object.entries(analysis.raw_data)
    .map(([dateStr, dayData]) => {
      const dailyKWh = calculateDailyKWh(dayData.quarters);
      return {
        date: dateStr,
        totalKWh: dailyKWh,
        status: getDayStatus(dailyKWh, contractedCapacity, targetSolutionKWh),
        threshold,
      };
    })
    .sort((a, b) => a.date.localeCompare(b.date));

  const greenDays = dayStatuses.filter(d => d.status === 'green').length;
  const redDays = dayStatuses.filter(d => d.status === 'red').length;
  const daysAboveThreshold = dayStatuses.filter(d => d.status === 'red');

  // Header
  pdf.setFontSize(20);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Stroomverbruik Rapportage', margin, y);
  y += 12;

  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`Analyse: ${analysis.name}`, margin, y);
  y += 7;
  
  if (analysis.organization_name) {
    pdf.text(`Organisatie: ${analysis.organization_name}`, margin, y);
    y += 7;
  }
  
  if (analysis.analysis_year) {
    pdf.text(`Jaar: ${analysis.analysis_year}`, margin, y);
    y += 7;
  }

  pdf.text(`Gegenereerd op: ${format(new Date(), 'd MMMM yyyy', { locale: nl })}`, margin, y);
  y += 15;

  // Threshold Configuration
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Threshold Configuratie', margin, y);
  y += 8;

  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`Contractvermogen: ${contractedCapacity} kWp`, margin, y);
  y += 6;
  pdf.text(`Beoogde oplossing (batterij): ${targetSolutionKWh.toLocaleString()} kWh`, margin, y);
  y += 6;
  pdf.text(`Dag threshold: ${formatKWh(threshold)} (24u × ${contractedCapacity} + ${targetSolutionKWh.toLocaleString()})`, margin, y);
  y += 12;

  // Year Summary
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Jaaroverzicht', margin, y);
  y += 8;

  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`Totaal jaarverbruik: ${formatKWh(totalKWh)}`, margin, y);
  y += 6;
  pdf.text(`Dagen onder threshold: ${greenDays} (groen)`, margin, y);
  y += 6;
  pdf.text(`Dagen boven threshold: ${redDays} (rood - generator nodig)`, margin, y);
  y += 12;

  // Monthly Breakdown Table
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Maandelijks Verbruik', margin, y);
  y += 10;

  // Table header
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Maand', margin, y);
  pdf.text('Verbruik (kWh)', margin + 50, y);
  pdf.text('% van totaal', margin + 100, y);
  y += 6;

  // Table rows
  pdf.setFont('helvetica', 'normal');
  DUTCH_MONTHS.forEach((monthName, index) => {
    const kWh = monthlyKWh[index] || 0;
    const percentage = totalKWh > 0 ? ((kWh / totalKWh) * 100).toFixed(1) : '0.0';
    
    pdf.text(monthName, margin, y);
    pdf.text(formatKWh(kWh), margin + 50, y);
    pdf.text(`${percentage}%`, margin + 100, y);
    y += 5;
  });
  y += 10;

  // Days Above Threshold
  if (daysAboveThreshold.length > 0) {
    // Check if we need a new page
    if (y > 220) {
      pdf.addPage();
      y = 20;
    }

    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text(`Dagen Boven Threshold (${redDays})`, margin, y);
    y += 10;

    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Datum', margin, y);
    pdf.text('Verbruik', margin + 50, y);
    pdf.text('Overschrijding', margin + 90, y);
    y += 5;

    pdf.setFont('helvetica', 'normal');
    
    // Limit to first 40 days to fit on page, or add pagination
    const maxDays = Math.min(daysAboveThreshold.length, 40);
    for (let i = 0; i < maxDays; i++) {
      const day = daysAboveThreshold[i];
      const overage = day.totalKWh - threshold;
      
      // Check for page break
      if (y > 280) {
        pdf.addPage();
        y = 20;
        
        // Re-add header on new page
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Datum', margin, y);
        pdf.text('Verbruik', margin + 50, y);
        pdf.text('Overschrijding', margin + 90, y);
        y += 5;
        pdf.setFont('helvetica', 'normal');
      }

      pdf.text(format(parseISO(day.date), 'd MMMM yyyy', { locale: nl }), margin, y);
      pdf.text(formatKWh(day.totalKWh), margin + 50, y);
      pdf.text(`+${formatKWh(overage)}`, margin + 90, y);
      y += 4;
    }

    if (daysAboveThreshold.length > maxDays) {
      y += 3;
      pdf.setFont('helvetica', 'italic');
      pdf.text(`... en nog ${daysAboveThreshold.length - maxDays} dagen meer`, margin, y);
    }
  }

  // Footer
  const pageCount = pdf.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i);
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'normal');
    pdf.text(
      `Pagina ${i} van ${pageCount}`,
      pageWidth - margin - 25,
      pdf.internal.pageSize.getHeight() - 10
    );
  }

  // Save
  const filename = `stroomverbruik_${analysis.name.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
  pdf.save(filename);
}

export default generateConsumptionReportPDF;
